import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactModal from 'react-modal';
import Swal from 'sweetalert2';
import useAxiosAdminFetch from '../../hooks/useAxiosAdminFetch';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import { useLanguage } from '../../hooks/LanguageContext';
import translations from '../../pages/admin/translations';
import { sanitizeNoticeHtml } from '../../utils/sanitizeNoticeHtml';
import LazySpinner from '../../pages/admin/LazySpinner';
import styles from '../../css/common/NoticePopup.module.css';

/**
 * 상점홈 공지 팝업. 원본: naegift-user-app/src/components/common/NoticePopup.tsx
 *
 * 흐름: GET /notice → 필수 공지(오래된 순) 순차 동의 → 다 끝나면 일반 공지 1건 → children 렌더.
 *  · 필수: [동의] 버튼만. 닫기·X·ESC·바깥클릭 없음(동의해야 넘어간다).
 *  · 일반: 닫기 + '오늘 그만보기'.
 *
 * 레이아웃: 팝업 높이는 뷰포트 기준(MODAL_STYLE.content.maxHeight)이고 본문만 내부 스크롤한다.
 * 헤더/힌트/버튼은 flex-shrink:0(CSS) 이라 본문이 아무리 길어도 밀려나지 않는다.
 */

/**
 * "끝까지 읽음" 판정 여유값(px).
 * 브라우저마다 scrollTop 이 소수점으로 떨어져 scrollTop+clientHeight 가 scrollHeight 에
 * 정확히 안 맞는 경우가 있어 2px 만 봐준다.
 */
const SCROLL_END_EPSILON = 2;

/** [notice-002] 필수 공지 "끝까지 읽음" 판정에서 본문 이미지 로드를 기다리는 최대 시간. */
const IMAGE_WAIT_LIMIT_MS = 10000;

/** 오늘 날짜 YYYY-MM-DD (기기 로컬 기준). */
function todayKey() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

/** 오늘 그만보기 저장 여부. 자정이 지나면 날짜가 달라져 자동으로 다시 뜬다. */
function isHiddenToday(noticeNo) {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(`notice_hide_${noticeNo}`) === todayKey();
  } catch {
    return false;
  }
}

function markHiddenToday(noticeNo) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(`notice_hide_${noticeNo}`, todayKey());
  } catch {
    /* ignore */
  }
}

/** 오버레이/컨테이너는 인라인 — ★zIndex: MobileHeader·하단네비 위로 확실히 올린다. */
const MODAL_STYLE = {
  overlay: {
    background: 'rgba(34, 34, 34, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '12px',
    zIndex: 1000,
  },
  content: {
    inset: 'unset',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: '420px',
    // ★뷰포트 기준 — 웹뷰 높이가 짧아도 팝업이 화면을 넘지 않는다(넘치는 건 본문이 스크롤로 흡수).
    maxHeight: '75vh',
    padding: '18px',
    borderRadius: '10px',
    overflow: 'hidden',
  },
};

function NoticePopup({ children }) {
  const { fetchData } = useAxiosAdminFetch();
  const { language } = useLanguage();
  const t = translations[language];

  const [phase, setPhase] = useState('loading');
  const [requiredNotices, setRequiredNotices] = useState([]);
  const [requiredIndex, setRequiredIndex] = useState(0);
  const [generalNotice, setGeneralNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hideToday, setHideToday] = useState(false);

  // 마운트 1회 조회. useAxiosAdminFetch = withCredentials:true 라 admin_accesstoken 쿠키가 실린다.
  //  ★조회도 로그인 필수(@Roles)라 미로그인/만료면 훅이 401 처리를 담당한다.
  useEffect(() => {
    let cancelled = false;

    const loadNotices = async () => {
      try {
        const response = await fetchData({
          method: 'get',
          url: `${process.env.REACT_APP_API_SERVER}notice`,
        });
        if (cancelled) return;

        // ResponseInterceptor 로 한 겹 감싸여 온다: { resultCode, resultMessage, data }
        const payload = response?.data?.data;
        const required = Array.isArray(payload?.requiredNotices)
          ? payload.requiredNotices.filter(n => n?.noticeNo != null)
          : [];
        const rawGeneral = payload?.generalNotice ?? null;
        // 오늘 그만보기 처리된 공지는 없는 것으로 본다.
        const general =
          rawGeneral && isHiddenToday(rawGeneral.noticeNo) ? null : rawGeneral;

        setRequiredNotices(required);
        setGeneralNotice(general);
        setRequiredIndex(0);
        if (required.length > 0) {
          setPhase('required');
        } else if (general) {
          setPhase('general');
        } else {
          setPhase('done');
        }
      } catch (e) {
        // 공지 조회 실패는 화면에 영향을 주지 않는다 — 조용히 팝업 없음 처리.
        console.error(e);
        if (!cancelled) setPhase('done');
      }
    };

    loadNotices();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentRequired =
    phase === 'required' ? (requiredNotices[requiredIndex] ?? null) : null;
  const activeNotice = phase === 'general' ? generalNotice : currentRequired;

  const sanitizedContent = useMemo(
    () =>
      sanitizeNoticeHtml(activeNotice?.content, activeNotice?.contentFormat),
    [activeNotice]
  );

  // ── 본문 끝까지 읽었는지 ─────────────────────────────────────────
  // 필수 공지는 본문을 끝까지 스크롤해야 동의가 열린다.
  // 스크롤이 아예 없는 짧은 본문(scrollHeight <= clientHeight)은 처음부터 읽은 것으로 본다.
  const bodyRef = useRef(null);
  const [readToEnd, setReadToEnd] = useState(false);

  /**
   * 본문 DOM 노드를 "상태로도" 들고 있는다.
   * ★이게 없으면 짧은 본문이 첫 공지일 때 동의가 영영 안 열린다:
   *   react-modal 은 포털이라 팝업이 처음 열리는 렌더에서는 아래 useLayoutEffect 가 돌 때
   *   bodyRef.current 가 아직 null 이다 → checkReadToEnd 도 ResizeObserver 도 그냥 빠져나가고,
   *   deps 가 그대로라 다시 돌지 않는다. 긴 본문은 사용자가 스크롤하면 onScroll 로 복구되지만
   *   짧은 본문은 스크롤 이벤트가 아예 없어 복구 경로가 없다.
   *   노드 부착을 상태로 승격시켜 두 effect 를 "노드가 실제로 붙은 뒤" 한 번 더 돌린다.
   */
  const [bodyEl, setBodyEl] = useState(null);
  const setBodyNode = useCallback(node => {
    bodyRef.current = node;
    setBodyEl(node);
  }, []);

  /**
   * [notice-002] 이미지 대기 상한이 지났는지. 로드도 실패도 끝나지 않는 이미지(느린 망 등) 때문에
   * 필수 공지 동의가 계속 잠기지 않게 한다. 공지가 바뀌면 아래 load/error effect 가 초기화한다.
   */
  const imageWaitExpiredRef = useRef(false);

  /** 한 번 도달하면 위로 되돌려도 계속 열린 상태를 유지한다(true 로만 바뀐다). */
  const checkReadToEnd = useCallback(() => {
    const el = bodyRef.current;
    if (!el) return;
    // [notice-002] 본문 이미지가 아직 뜨지 않았으면 판정하지 않는다 — 로드 전에는 본문이 짧아
    //  "스크롤 없음"으로 바로 열리고, 이 판정은 되돌리지 않아 이미지를 보기 전에 동의가 열린다.
    //  img.complete 는 로드 실패(깨진 이미지)여도 true 라 실패한 이미지 때문에 잠기지 않는다.
    //  로드·실패 시점의 재판정은 아래 load/error 리스너가 한다.
    if (
      !imageWaitExpiredRef.current &&
      Array.from(el.querySelectorAll('img')).some(img => !img.complete)
    ) {
      return;
    }
    const noScroll = el.scrollHeight - el.clientHeight <= SCROLL_END_EPSILON;
    const atEnd =
      el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_END_EPSILON;
    if (noScroll || atEnd) {
      setReadToEnd(true);
    }
  }, []);

  /**
   * 공지가 바뀌면 스크롤 위치와 판정을 초기화한다.
   * ★scrollTop 리셋이 필요한 이유: 다음 필수 공지로 넘어가도 React 는 같은 DOM 노드를
   *   재사용하므로 이전 공지에서 맨 아래까지 내려둔 스크롤이 그대로 남는다 —
   *   그러면 새 공지를 안 읽었는데 "끝까지 읽음"으로 판정된다.
   * useLayoutEffect: 페인트 전에 정리해 활성 상태가 한 프레임 깜빡이지 않게.
   */
  useLayoutEffect(() => {
    setReadToEnd(false);
    const el = bodyRef.current;
    if (el) el.scrollTop = 0;
    checkReadToEnd();
    // bodyEl: 노드가 붙는 시점에 한 번 더 판정한다(위 setBodyNode 주석 참고).
  }, [activeNotice?.noticeNo, sanitizedContent, checkReadToEnd, bodyEl]);

  /**
   * 본문 높이가 나중에 변하는 경우에도 다시 판정한다 —
   * 웹폰트 로딩으로 본문이 늘어나거나, 웹뷰 회전·키보드로 팝업 높이가 바뀌는 경우.
   * (ResizeObserver 미지원 환경에서는 조용히 건너뛴다 — 스크롤 이벤트 판정은 그대로 동작)
   */
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => checkReadToEnd());
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
    // bodyEl: 노드가 붙은 뒤에야 observe 할 수 있다(위 setBodyNode 주석 참고).
  }, [activeNotice?.noticeNo, sanitizedContent, checkReadToEnd, bodyEl]);

  /**
   * [notice-002] 본문 이미지가 로드되거나 실패하면 다시 판정한다.
   * load/error 는 버블링되지 않아 캡처 단계(true)로 받는다. 깨진 이미지는 높이가 안 바뀌어
   * ResizeObserver 가 울리지 않으므로 이 리스너가 있어야 잠기지 않는다.
   * 10초가 지나도 끝나지 않은 이미지는 기다리지 않고 판정한다(IMAGE_WAIT_LIMIT_MS).
   */
  useEffect(() => {
    imageWaitExpiredRef.current = false;
    const el = bodyRef.current;
    if (!el) return undefined;
    el.addEventListener('load', checkReadToEnd, true);
    el.addEventListener('error', checkReadToEnd, true);
    const timer = window.setTimeout(() => {
      imageWaitExpiredRef.current = true;
      checkReadToEnd();
    }, IMAGE_WAIT_LIMIT_MS);
    return () => {
      window.clearTimeout(timer);
      el.removeEventListener('load', checkReadToEnd, true);
      el.removeEventListener('error', checkReadToEnd, true);
    };
  }, [activeNotice?.noticeNo, sanitizedContent, checkReadToEnd, bodyEl]);

  /** 현재 필수 공지 처리 완료 → 다음 필수, 없으면 일반, 그것도 없으면 종료. */
  const advanceFromRequired = useCallback(() => {
    const next = requiredIndex + 1;
    if (next < requiredNotices.length) {
      setRequiredIndex(next);
    } else {
      setPhase(generalNotice ? 'general' : 'done');
    }
  }, [requiredIndex, requiredNotices.length, generalNotice]);

  const handleRequiredConfirm = useCallback(async () => {
    if (!currentRequired || busy) return;

    setBusy(true);
    try {
      const response = await fetchData({
        method: 'post',
        url: `${process.env.REACT_APP_API_SERVER}notice/consent`,
        data: { noticeNo: currentRequired.noticeNo },
      });
      const status = response?.status;
      if (status === 200 || status === 201) {
        advanceFromRequired();
      } else {
        await Swal.fire({ icon: 'error', title: t.noticeConsentFailed });
      }
    } catch (e) {
      console.error(e);
      await Swal.fire({ icon: 'error', title: t.noticeConsentFailed });
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRequired, busy, advanceFromRequired, t]);

  const handleGeneralClose = useCallback(() => {
    if (hideToday && generalNotice) {
      markHiddenToday(generalNotice.noticeNo);
    }
    setPhase('done');
  }, [hideToday, generalNotice]);

  /**
   * 배경 스크롤·터치 잠금.
   * ★훅이라 아래 조기 return 보다 반드시 위에서 호출해야 한다(훅 순서 규칙).
   * ★bodyRef 를 넘겨 팝업 본문 안에서 난 터치만 통과시킨다 — 긴 필수 공지 스크롤은 살아 있어야
   *   "끝까지 읽어야 동의" 관문을 통과할 수 있다.
   */
  const isPopupOpen =
    phase !== 'loading' && phase !== 'done' && Boolean(activeNotice);
  useBodyScrollLock(isPopupOpen, bodyRef);

  if (phase === 'loading') {
    return <LazySpinner />;
  }
  if (phase === 'done' || !activeNotice) {
    // 처리할 공지가 없으면 통과시킨다(자식 보류 해제).
    return <>{children}</>;
  }

  const isRequired = phase === 'required';
  // 필수 공지에서 본문을 끝까지 안 읽었으면 동의를 잠근다(일반 공지는 잠그지 않는다).
  const consentLocked = isRequired && !readToEnd;

  return (
    <ReactModal
      isOpen
      style={MODAL_STYLE}
      // 필수 공지는 동의 전에는 어떤 경로로도 닫히면 안 된다.
      shouldCloseOnEsc={!isRequired}
      shouldCloseOnOverlayClick={!isRequired}
      onRequestClose={isRequired ? undefined : handleGeneralClose}
      contentLabel={activeNotice.title || ''}
    >
      <div className={styles.header}>
        <h2 className={styles.title}>{activeNotice.title}</h2>
        {isRequired && requiredNotices.length > 1 && (
          <div className={styles.stepText}>
            {t.noticeRequiredLabel} {requiredIndex + 1}/{requiredNotices.length}
          </div>
        )}
      </div>

      {/* 본문은 운영자가 쓴 HTML — sanitizeNoticeHtml 로 화이트리스트 통과분만 렌더한다.
          이 div 만 스크롤한다(헤더/버튼은 고정). 안쪽 래퍼는 ResizeObserver 대상. */}
      <div className={styles.body} ref={setBodyNode} onScroll={checkReadToEnd}>
        <div dangerouslySetInnerHTML={{ __html: sanitizedContent }} />
      </div>

      {consentLocked && (
        <div className={styles.scrollHint}>{t.noticeScrollToEnd}</div>
      )}

      {!isRequired && (
        <div
          className={styles.hideTodayRow}
          onClick={() => setHideToday(v => !v)}
        >
          <input
            type="checkbox"
            checked={hideToday}
            onChange={() => setHideToday(v => !v)}
            onClick={e => e.stopPropagation()}
            className={styles.hideTodayCheckbox}
          />
          <span className={styles.hideTodayText}>{t.noticeHideToday}</span>
        </div>
      )}

      <div className={styles.footer}>
        {isRequired ? (
          <button
            type="button"
            className={styles.button}
            onClick={handleRequiredConfirm}
            disabled={busy || consentLocked}
          >
            {busy ? t.processing : t.noticeAgree}
          </button>
        ) : (
          <button
            type="button"
            className={styles.button}
            onClick={handleGeneralClose}
          >
            {t.noticeClose}
          </button>
        )}
      </div>
    </ReactModal>
  );
}

/**
 * 팝업 렌더 예외가 화면 전체를 언마운트시키지 않도록 격리한다.
 * (비동기 실패는 각 호출부 try/catch 가 이미 삼킨다 — 이건 렌더/훅 예외용 최후 방어선)
 */
export default class NoticePopupBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.error('[notice-popup]', error);
  }

  render() {
    // ★fail-open: 팝업이 터져도 페이지를 막지 않는다(백지 방지).
    if (this.state.failed) return <>{this.props.children ?? null}</>;
    return <NoticePopup>{this.props.children}</NoticePopup>;
  }
}
